import React, { useState, useMemo, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import EmployeeListTable from './components/EmployeeListTable';
import EmployeeForm from './components/EmployeeForm';
import BulkImportModal from './components/BulkImportModal';
import { employeesService, departmentsService, rolesService } from '../../services/supabaseService';

const EmployeeManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [newlyImportedIds, setNewlyImportedIds] = useState([]);

  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSettings, setLoadingSettings] = useState(true);

  const statuses = ['Active', 'On Leave', 'Inactive'];

  // Load employees and settings from Supabase on component mount
  useEffect(() => {
    loadEmployees();
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoadingSettings(true);
      const [depts, rolesData] = await Promise.all([
        departmentsService?.getAll(),
        rolesService?.getAll()
      ]);
      setDepartments(depts?.map(d => d?.name) || []);
      setRoles(rolesData?.map(r => r?.name) || []);
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoadingSettings(false);
    }
  };

  const loadEmployees = async () => {
    try {
      setLoading(true);
      const data = await employeesService?.getAll();
      setEmployees(data);
    } catch (error) {
      console.error('Error loading employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredEmployees = useMemo(() => {
    return employees?.filter((emp) => {
      const matchesSearch = emp?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      emp?.employeeId?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      emp?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase());
      const matchesDepartment = departmentFilter === 'all' || emp?.department === departmentFilter;
      const matchesRole = roleFilter === 'all' || emp?.role === roleFilter;
      const matchesStatus = statusFilter === 'all' || emp?.status === statusFilter;
      return matchesSearch && matchesDepartment && matchesRole && matchesStatus;
    });
  }, [employees, searchTerm, departmentFilter, roleFilter, statusFilter]);

  const sortedEmployees = useMemo(() => {
    const sorted = [...filteredEmployees];
    sorted?.sort((a, b) => {
      const aValue = a?.[sortConfig?.key];
      const bValue = b?.[sortConfig?.key];
      if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredEmployees, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleAddEmployee = () => {
    setEditingEmployee(null);
    setShowForm(true);
  };

  const handleEditEmployee = (employee) => {
    setEditingEmployee(employee);
    setShowForm(true);
  };

  const handleDeleteEmployee = async (employeeId) => {
    if (window.confirm('Are you sure you want to delete this employee?')) {
      try {
        const success = await employeesService?.delete(employeeId);
        if (success) {
          setEmployees((prev) => prev?.filter((emp) => emp?.id !== employeeId));
          setSelectedEmployees((prev) => prev?.filter((id) => id !== employeeId));
        }
      } catch (error) {
        console.error('Error deleting employee:', error);
      }
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(`Are you sure you want to delete ${selectedEmployees?.length} employee(s)?`)) {
      try {
        for (const id of selectedEmployees) {
          await employeesService?.delete(id);
        }
        setEmployees((prev) => prev?.filter((emp) => !selectedEmployees?.includes(emp?.id)));
        setSelectedEmployees([]);
      } catch (error) {
        console.error('Error bulk deleting employees:', error);
      }
    }
  };

  const handleBulkDepartmentChange = async (newDepartment) => {
    try {
      for (const id of selectedEmployees) {
        await employeesService?.update(id, { department: newDepartment });
      }
      setEmployees((prev) => prev?.map((emp) =>
      selectedEmployees?.includes(emp?.id) ? { ...emp, department: newDepartment } : emp
      ));
      setSelectedEmployees([]);
    } catch (error) {
      console.error('Error bulk updating department:', error);
    }
  };

  const handleBulkStatusChange = async (newStatus) => {
    try {
      for (const id of selectedEmployees) {
        await employeesService?.update(id, { status: newStatus });
      }
      setEmployees((prev) => prev?.map((emp) =>
      selectedEmployees?.includes(emp?.id) ? { ...emp, status: newStatus } : emp
      ));
      setSelectedEmployees([]);
    } catch (error) {
      console.error('Error bulk updating status:', error);
    }
  };

  const handleSaveEmployee = async (employeeData) => {
    try {
      if (editingEmployee) {
        const updatedEmployee = await employeesService?.update(editingEmployee?.id, employeeData);
        if (updatedEmployee) {
          setEmployees((prev) => prev?.map((emp) =>
          emp?.id === editingEmployee?.id ? updatedEmployee : emp
          ));
        }
      } else {
        const newEmployee = await employeesService?.create({
          ...employeeData,
          employeeId: `EMP-2024-${String(employees?.length + 1)?.padStart(3, '0')}`,
          complianceStatus: 'Good',
          avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_152474a04-1767640485316.png",
          avatarAlt: 'Default employee avatar',
          currentPoints: 0,
          complianceScore: 100,
          recentViolations: 0,
          trend: 'stable'
        });
        if (newEmployee) {
          setEmployees((prev) => [...prev, newEmployee]);
        }
      }
      setShowForm(false);
      setEditingEmployee(null);
    } catch (error) {
      console.error('Error saving employee:', error);
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingEmployee(null);
  };

  const handleBulkImport = async (employeesData) => {
    try {
      const result = await employeesService?.bulkCreate(employeesData);
      
      // Handle the new response format
      const importedEmployees = result?.imported || [];
      const duplicateCount = result?.duplicateCount || 0;
      const duplicates = result?.duplicates || [];
      
      if (importedEmployees?.length > 0) {
        // Store newly imported IDs for highlighting
        const newIds = importedEmployees?.map(emp => emp?.id);
        setNewlyImportedIds(newIds);
        
        // Refresh the employee list
        await loadEmployees();
        
        // Show success message with details
        if (duplicateCount > 0) {
          alert(
            `Successfully imported ${importedEmployees?.length} employee(s).\n\n` +
            `${duplicateCount} employee(s) were skipped because they already exist in the database.\n` +
            `Duplicate emails: ${duplicates?.slice(0, 3)?.join(', ')}${duplicates?.length > 3 ? '...' : ''}`
          );
        } else {
          alert(`Successfully imported ${importedEmployees?.length} employee(s)!`);
        }
        
        // Clear highlights after 5 seconds
        setTimeout(() => {
          setNewlyImportedIds([]);
        }, 5000);
      } else if (duplicateCount > 0) {
        // All were duplicates
        alert(
          `No employees were imported. All ${duplicateCount} employee(s) already exist in the database.\n\n` +
          `Duplicate emails: ${duplicates?.slice(0, 5)?.join(', ')}${duplicates?.length > 5 ? '...' : ''}`
        );
      }
      
      setShowBulkImport(false);
    } catch (error) {
      console.error('Error importing employees:', error);
      alert(`Failed to import employees: ${error?.message || 'Unknown error occurred'}`);
    }
  };

  const breadcrumbItems = [
  { label: 'Home', path: '/' },
  { label: 'Employee Management', path: '/employee-management' }];


  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16">
        <div className="px-6 py-6">
          <Breadcrumb items={breadcrumbItems} />
          
          {loading ?
          <div className="flex items-center justify-center py-24">
              <div className="text-center">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-lg text-muted-foreground">Loading employees...</p>
              </div>
            </div> :

          <>
              <div className="flex items-center justify-between mb-6 mt-4">
                <div>
                  <h1 className="text-3xl font-bold text-foreground mb-2">Employee Management</h1>
                  <p className="text-muted-foreground">Manage employee records, departments, and organizational structure</p>
                </div>
                <div className="flex items-center gap-3">
                  <Button
                  variant="outline"
                  onClick={() => setShowBulkImport(true)}
                  className="flex items-center gap-2">
                    
                      <Icon name="Upload" size={18} />
                      Bulk Import
                  </Button>
                  <Button
                  onClick={handleAddEmployee}
                  className="flex items-center gap-2">
                    
                      <Icon name="Plus" size={18} />
                      Add Employee
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                <div className={showForm ? 'lg:col-span-8' : 'lg:col-span-12'}>
                  <div className="bg-card rounded-lg shadow-sm border border-border p-6">
                    <div className="flex flex-col lg:flex-row gap-4 mb-6">
                      <div className="flex-1">
                        <Input
                        placeholder="Search by name, ID, or email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e?.target?.value)}
                        leftIcon="Search" />
                        
                      </div>
                      <div className="flex gap-3">
                        <select
                        value={departmentFilter}
                        onChange={(e) => setDepartmentFilter(e?.target?.value)}
                        className="px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary">
                          
                          <option value="all">All Departments</option>
                          {departments?.map((dept) =>
                        <option key={dept} value={dept}>{dept}</option>
                        )}
                        </select>
                        <select
                        value={roleFilter}
                        onChange={(e) => setRoleFilter(e?.target?.value)}
                        className="px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary">
                          
                          <option value="all">All Roles</option>
                          {roles?.map((role) =>
                        <option key={role} value={role}>{role}</option>
                        )}
                        </select>
                        <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e?.target?.value)}
                        className="px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary">
                          
                          <option value="all">All Statuses</option>
                          {statuses?.map((status) =>
                        <option key={status} value={status}>{status}</option>
                        )}
                        </select>
                      </div>
                    </div>

                    {selectedEmployees?.length > 0 &&
                  <div className="flex items-center gap-3 mb-4 p-3 bg-primary/5 rounded-lg border border-primary/20">
                        <span className="text-sm font-medium text-foreground">
                          {selectedEmployees?.length} employee(s) selected
                        </span>
                        <div className="flex gap-2 ml-auto">
                          <select
                        onChange={(e) => {
                          if (e?.target?.value) {
                            handleBulkDepartmentChange(e?.target?.value);
                            e.target.value = '';
                          }
                        }}
                        className="px-3 py-1 text-sm border border-border rounded bg-background text-foreground">
                          
                            <option value="">Change Department</option>
                            {departments?.map((dept) =>
                        <option key={dept} value={dept}>{dept}</option>
                        )}
                          </select>
                          <select
                        onChange={(e) => {
                          if (e?.target?.value) {
                            handleBulkStatusChange(e?.target?.value);
                            e.target.value = '';
                          }
                        }}
                        className="px-3 py-1 text-sm border border-border rounded bg-background text-foreground">
                          
                            <option value="">Change Status</option>
                            {statuses?.map((status) =>
                        <option key={status} value={status}>{status}</option>
                        )}
                          </select>
                          <Button
                        variant="outline"
                        size="sm"
                        onClick={handleBulkDelete}
                        className="text-destructive hover:bg-destructive/10">
                          
                            <Icon name="Trash2" size={16} />
                          </Button>
                        </div>
                      </div>
                  }

                    <EmployeeListTable
                    employees={sortedEmployees}
                    selectedEmployees={selectedEmployees}
                    onSelectEmployees={setSelectedEmployees}
                    onSort={handleSort}
                    sortConfig={sortConfig}
                    onEdit={handleEditEmployee}
                    onDelete={handleDeleteEmployee}
                    newlyImportedIds={newlyImportedIds}
                   />

                    <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
                      <span>Showing {sortedEmployees?.length} of {employees?.length} employees</span>
                    </div>
                  </div>
                </div>

                {showForm &&
              <div className="lg:col-span-4">
                    <div className="bg-card rounded-lg shadow-sm border border-border p-6 sticky top-20">
                      <EmployeeForm
                    employee={editingEmployee}
                    departments={departments}
                    roles={roles}
                    statuses={statuses}
                    onSave={handleSaveEmployee}
                    onCancel={handleCancelForm} />
                    
                    </div>
                  </div>
              }
              </div>
            </>
          }
        </div>
      </div>
      {showBulkImport &&
      <BulkImportModal
        onClose={() => setShowBulkImport(false)}
        onImport={handleBulkImport}
        departments={departments}
        roles={roles} />

      }
    </div>);

};

export default EmployeeManagement;